<template>
  <div>
    <el-row>
      <el-col :span="24">
        <el-card class="data" v-loading="loading">
          <div class="data-title flex-space-between" slot="header">
            <p>Cults</p>
          </div>
          <el-table 
            :data="cults"
            :default-sort="{ prop: order_column, order: order_type }"
            class="w100"
            style="width: 100%"
            stripe
          >
            <el-table-column prop="id" label="ID" sortable></el-table-column>
            <el-table-column prop="name" label="Name" sortable></el-table-column>
            <el-table-column label="Owner" sortable>
              <template slot-scope="scope">
                <a href="#" @click.prevent="showUserModal(scope.row.user)">{{ scope.row.user.email }}</a>
              </template>
            </el-table-column>
            <el-table-column prop="task_count" label="Task Count" sortable align="center"></el-table-column>
            <el-table-column prop="submission_count" label="Submission Count" sortable align="center"></el-table-column>
            <el-table-column prop="ended_quests" label="Ended Quests" sortable align="center"></el-table-column>
            <el-table-column prop="created_at" label="Created At" sortable>
              <template slot-scope="scope">
                {{ scope.row.created_at | datetime }}
              </template>
            </el-table-column>
            <el-table-column label="Actions" align="right">
              <template slot-scope="scope">
                <el-tooltip content="View" placement="top">
                  <el-button type="primary" circle size="small" icon="el-icon-view" @click="handleView(scope.row)"></el-button>
                </el-tooltip>
                <el-tooltip content="Edit" placement="top">
                  <el-button type="warning" circle size="small" icon="el-icon-edit" @click="handleEdit(scope.row)"></el-button>
                </el-tooltip>
                <el-tooltip content="Delete" placement="top">
                  <el-button type="danger" circle size="small" icon="el-icon-delete" @click="handleDelete(scope.row)"></el-button>
                </el-tooltip>
              </template>
            </el-table-column>
          </el-table>
          <el-pagination
            class="float-right mt20"
            background
            @size-change="sizeChange"
            @current-change="currentChange"
            :current-page.sync="page"
            :page-sizes="[5, 10, 25, 50, 100]"
            :page-size="page_size"
            :page-count="page_count"
            layout="total, sizes, prev, pager, next, jumper"
            :total="total"
          ></el-pagination>
        </el-card>
      </el-col>
    </el-row>

    <!-- User Info Modal -->
    <el-dialog
      title="User Information"
      :visible.sync="userModalVisible"
      width="30%"
      center
    >
      <div v-if="selectedUser">
        <p><strong>ID:</strong> {{ selectedUser.id }}</p>
        <p><strong>Email:</strong> {{ selectedUser.email }}</p>
        <p><strong>First Name:</strong> {{ selectedUser.first_name }}</p>
        <p><strong>Last Name:</strong> {{ selectedUser.surname }}</p>
        <p><strong>User Level:</strong> {{ selectedUser.user_level }}</p>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="userModalVisible = false">Close</el-button>
      </span>
    </el-dialog>

    <!-- Edit Cult Modal -->
    <el-dialog
      title="Edit Cult"
      :visible.sync="editModalVisible"
      width="50%"
      :before-close="closeEditModal"
    >
      <el-form v-if="selectedCult" ref="cultForm" :model="selectedCult" label-position="top">
        <el-form-item label="Title" prop="name" :rules="[{ required: true, message: 'Title is required' }]">
          <el-input v-model="selectedCult.name"></el-input>
        </el-form-item>

        <el-form-item label="Description" prop="description">
          <el-input type="textarea" v-model="selectedCult.description" :rows="4"></el-input>
        </el-form-item>

        <el-form-item label="Owner">
          <div class="owner-input-group">
            <el-input v-model="selectedCult.user.email" disabled></el-input>
            <el-button type="primary" class="change-owner-btn">Change Owner</el-button>
          </div>
        </el-form-item>
      </el-form>

      <span slot="footer" class="dialog-footer">
        <el-button @click="closeEditModal">Close</el-button>
        <el-button type="primary" @click="saveCult" :loading="loading">Save</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex'

export default {
  data() {
    return {
      userModalVisible: false,
      selectedUser: null
    }
  },

  computed: {
    ...mapState('cults', [
      'cults',
      'loading',
      'page',
      'page_size',
      'total',
      'page_count',
      'order_column',
      'order_type',
      'selectedCult',
      'editModalVisible'
    ])
  },

  methods: {
    ...mapActions('cults', ['read', 'update']),
    ...mapMutations('cults', [
      'setPage',
      'setPageSize',
      'setOrdering',
      'setSelectedCult',
      'setEditModalVisible'
    ]),

    sizeChange(page_size) {
      this.setPageSize(page_size)
      this.read()
    },

    currentChange(page) {
      this.setPage(page)
      this.read()
    },

    sort(d) {
      this.setOrdering({
        column: d.prop,
        type: d.order === 'ascending' ? 'asc' : 'desc'
      })
      this.read()
    },

    formatNumber(num) {
      return num ? num.toLocaleString() : 0
    },

    showUserModal(user) {
      this.selectedUser = user
      this.userModalVisible = true
    },

    handleView(row) {
      // Navigate to the quests page for this cult
      this.$router.push(`/cults/${row.id}/quests`)
    },

    handleEdit(row) {
      // Make a deep copy of the row to avoid directly modifying the store data
      const cultCopy = JSON.parse(JSON.stringify(row));
      this.setSelectedCult(cultCopy);
      this.setEditModalVisible(true);
    },

    closeEditModal() {
      this.setEditModalVisible(false);
      this.setSelectedCult(null);
    },

    saveCult() {
      if (!this.selectedCult) return;

      this.$refs.cultForm.validate(valid => {
        if (valid) {
          this.update(this.selectedCult)
            .then(() => {
              this.$message({
                message: 'Cult updated successfully',
                type: 'success'
              });
            })
            .catch(error => {
              this.$message({
                message: 'Failed to update cult',
                type: 'error'
              });
              console.error('Error updating cult:', error)
            });
        } else {
          return false;
        }
      });
    },

    handleDelete(row) {
      this.$confirm('Are you sure you want to delete this cult?', 'Warning', {
        confirmButtonText: 'OK',
        cancelButtonText: 'Cancel',
        type: 'warning'
      }).then(() => {
        // Placeholder for delete action
        console.log('Delete cult:', row)
        this.$message({
          type: 'success',
          message: 'Delete functionality will be implemented in the future'
        })
      }).catch(() => {
        this.$message({
          type: 'info',
          message: 'Delete canceled'
        })
      })
    }
  },

  created() {
    this.read()
  },

  beforeRouteLeave(to, from, next) {
    this.$store.commit('cults/reset')
    next()
  }
}
</script>

<style scoped>
.data-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.mr5 {
  margin-right: 5px;
}

.mt20 {
  margin-top: 20px;
}

.float-right {
  float: right;
}

.owner-input-group {
  display: flex;
  align-items: center;
}

.owner-input-group .el-input {
  flex: 1;
  margin-right: 10px;
}

.change-owner-btn {
  white-space: nowrap;
}
</style>
